import { useEffect } from "react";

import { photoStorage } from "@/Services/PhotoStorage";

/**
 * Closes the IDB connection when the tab goes to the background and
 * re-opens it on return. While reconnecting, `idbReadyGate` is closed so
 * any concurrent `handleBeforeUpload` calls will await reconnection instead
 * of hitting a dead connection and throwing UnknownError.
 */
export const useIndexedDBFocus = () => {
    useEffect(() => {
        const handleVisibilityChange = async () => {
            if (document.hidden) {
                // Close gate FIRST — block any uploads that might start
                // in the brief window before the connection actually closes.
                photoStorage?.closeGate?.();
                photoStorage?.close?.();
            } else {
                // Gate is already closed from the hidden branch.
                // Re-init, then open gate so queued uploads can proceed.
                try {
                    await photoStorage.init?.();
                } catch (error) {
                    console.error(
                        "Failed to reinitialize IndexedDB on visibility:",
                        error,
                    );
                    // Even on failure, open the gate so callers can receive
                    // the real IDB error rather than hanging forever.
                } finally {
                    photoStorage?.openGate?.();
                }
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            document.removeEventListener(
                "visibilitychange",
                handleVisibilityChange,
            );
        };
    }, []);
};
